import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { Head } from '@eneco/next';

interface MetaTag {
  tag?: string;
  attributes?: Record<string, string>;
}

/**
 * A tag's key by what it is (`og:title`, `description`, `canonical`), as `CmsRouteContainer`'s `PageHead`
 * keys its own: Next's `Head` keeps one tag per key, so the page head and Sitecore's tags do not both
 * end up in it. Sitecore's are rendered later and win, as on the legacy lane.
 */
const tagKey = ({ tag, attributes = {} }: MetaTag): string =>
  attributes.property ?? attributes.name ?? (attributes.rel === 'canonical' ? 'canonical' : `${tag}-${JSON.stringify(attributes)}`);

/**
 * `MetaData` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/system/src/MetaData/MetaData.tsx`: the tags Sitecore's
 * `MetaDataContentResolver` computes on its server, rendered into the head as it hands them over. Its
 * fields are a list, which the Sitecore adapter hands over as an object keyed by position (`"0"`, `"1"`, …).
 *
 * The Sanity block has no fields (ADR-0104): there the head comes from the page's own SEO fields, which
 * `CmsRouteContainer`'s `PageHead` renders on both lanes, so this renders nothing.
 */
const MetaData: FC = () => {
  const { page } = useCms();
  const content = useContent<Record<string, MetaTag | null>>();
  const tags = page.context.cmsSource === 'sanity' ? [] : Object.values(content);

  if (tags.length === 0) return null;

  return (
    <Head>
      {tags.map(property => {
        if (!property?.tag) return null;

        const TagName = property.tag;
        return <TagName key={tagKey(property)} {...(property?.attributes ?? {})} />;
      })}
    </Head>
  );
};

export default MetaData;
